import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { bulkInvoiceDetails } from '../../../../redux/actions/SocietyPortal/InvoiceAction/InvoiceAction';
import InvoiceTemplateRenderer from './components/InvoiceTemplateRenderer';
import usePrintIsolation from '../../../../utils/usePrintIsolation';

const BulkPrintInvoices = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { bulkDetailsData, bulkDetailsLoading } = useSelector(state => state.InvoiceReducer || {});

    const printRef = useRef(null);

    const invoiceIds = searchParams.get('ids')?.split(',').map(Number).filter(Boolean) || [];

    useEffect(() => {
        if (invoiceIds.length > 0) {
            dispatch(bulkInvoiceDetails({ invoice_ids: invoiceIds }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, invoiceIds.join(',')]);

    const handlePrint = () => window.print();
    const handleBack = () => navigate(-1);

    const invoices = bulkDetailsData || [];

    // Hide the surrounding portal chrome (sidebar/header/footer/background) while printing.
    usePrintIsolation(printRef, invoices.length > 0);

    if (bulkDetailsLoading || invoices.length === 0) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50">
                <div className="tw-text-center">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-indigo-600 tw-mx-auto"></div>
                    <p className="tw-text-gray-500 tw-mt-4 tw-text-sm">
                        {bulkDetailsLoading ? `Loading ${invoiceIds.length} invoice(s)...` : 'No invoices to print'}
                    </p>
                    {!bulkDetailsLoading && invoices.length === 0 && invoiceIds.length === 0 && (
                        <button onClick={handleBack} className="tw-mt-4 tw-text-sm tw-text-indigo-600 hover:tw-underline">Go back</button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                @media print {
                    body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .no-print { display: none !important; }
                    /* Hide app chrome (sidebar/header/footer) and stop ancestors clipping content */
                    .print-hide { display: none !important; }
                    .print-expand { height: auto !important; min-height: 0 !important; max-height: none !important; overflow: visible !important; }
                    /* Drop the on-screen gray page background so it doesn't print */
                    .print-area { background: #fff !important; padding: 0 !important; min-height: 0 !important; }
                    .print-invoice-page { page-break-after: always; margin: 0; padding: 6mm; box-shadow: none !important; border: none !important; }
                    .print-invoice-page:last-child { page-break-after: auto; }
                    @page { size: A4; margin: 8mm; }
                }
            `}</style>

            {/* Action Bar */}
            <div className="no-print tw-bg-white tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10 tw-shadow-sm">
                <div className="tw-max-w-4xl tw-mx-auto tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button onClick={handleBack} className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500">
                            <Icon icon="mdi:arrow-left" className="tw-text-xl" />
                        </button>
                        <div>
                            <h1 className="tw-text-lg tw-font-bold tw-text-gray-900">Bulk Print Invoices</h1>
                            <p className="tw-text-xs tw-text-gray-500">{invoices.length} invoice(s)</p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button onClick={handleBack} className="tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50">
                            Back
                        </button>
                        <button onClick={handlePrint} className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-bg-indigo-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-semibold hover:tw-bg-indigo-700 tw-shadow-sm">
                            <Icon icon="mdi:printer" className="tw-text-lg" /> Print All ({invoices.length})
                        </button>
                    </div>
                </div>
            </div>

            {/* Invoice Pages */}
            <div ref={printRef} className="print-area tw-bg-gray-100 tw-min-h-screen tw-py-8">
                {invoices.map((invoice, pageIdx) => (
                    <InvoiceTemplateRenderer
                        key={invoice.invoice_id || pageIdx}
                        invoice={invoice}
                        pageIdx={pageIdx}
                        totalPages={invoices.length}
                        className={`print-invoice-page tw-max-w-4xl tw-mx-auto tw-bg-white tw-shadow-lg tw-border tw-border-gray-200 ${pageIdx < invoices.length - 1 ? 'tw-mb-8' : ''}`}
                    />
                ))}
            </div>
        </>
    );
};

export default BulkPrintInvoices;
